import { DateTime } from 'luxon';

const defaultTimezone = 'America/Chicago';
const spacesRegex = /\s+/g;
const timezoneInfoRegex = /(UTC|GMT|PST|PDT|MST|MDT|CST|CDT|EST|EDT)/;
const offsetSuffixRegex = /\+[\d:]+$/;
const luxonDateFormats: string[] = [
	// formats we for sure want to support
	'M/d/yyyy h:mm:ss a',
	'M-d-yyyy h:mm:ss a',
	// help out when no space before am/pm
	// since some browsers don't seem to like this.
	'M/d/yyyy h:mma',
	'M/d/yyyy h:mm:ssa',
	'M-d-yyyy h:mma',
	'M-d-yyyy h:mm:ssa'
];

export function getLocalTimezoneOrDefault(): string {
	return DateTime.now().zoneName ?? defaultTimezone;
}

export function parseDateString(dateString: string | nundefined, timezone: string): ParsedDateResult {
    dateString = (dateString ?? '').trim();
    
    if (!dateString)
    	return { type: 'none' };
    
    try {
		// first try ISO
		const isoResult = DateTime.fromISO(dateString, { zone: timezone });
		if (isoResult.isValid)
			return getParseDateStringResultFromValidDateTime(isoResult);
		
		// then try RFC2822
		const rfcResult = DateTime.fromRFC2822(dateString, { zone: timezone });
		if (rfcResult.isValid)
			return getParseDateStringResultFromValidDateTime(rfcResult);
		
		// then try SQL
		const sqlResult = DateTime.fromSQL(dateString, { zone: timezone });
		if (sqlResult.isValid)
			return getParseDateStringResultFromValidDateTime(sqlResult);
		
		// wasn't a known, official format so we'll try some other formats
		
		// first, normalize spacing
		dateString = dateString.replace(spacesRegex, ' ');
		
		// next we'll try a list of known, common formats. we do this
		// because our last resort is to use Date.parse but that isn't
		// consistent across browsers so we first try a set of formats
		// that we for sure what to support to make sure they work cross-browser.
		for (let format of luxonDateFormats) {
			const formatResult = DateTime.fromFormat(dateString, format, { zone: timezone });
			if (formatResult.isValid)
				return getParseDateStringResultFromValidDateTime(formatResult);
		}
		
		// last resort is to use browser Date.parse. it's not great because
		// it can vary from browser to browser but it is most permissive in
		// terms of parsing.
		const parsed = Date.parse(dateString);
		if (isNaN(parsed))
			return { type: 'invalid' };
		
		// depending on if the date string was an 'absolute' date
		// we want to either leave the underlying timestamp unchanged
		// and simply display the date in the target timezone or if the
		// date string was 'relative' then we want to change the date
		// to the target timezone while leaving 'local time' unchanged
		const dateResult = timezoneInfoRegex.test(dateString) || offsetSuffixRegex.test(dateString)
		 	? DateTime.fromJSDate(new Date(parsed), { zone: timezone })
			: DateTime.fromJSDate(new Date(parsed)).setZone(timezone, { keepLocalTime: true });
		
		if (dateResult.isValid) {
			return getParseDateStringResultFromValidDateTime(dateResult);
		} else {
			return { type: 'invalid' };
		}
	} catch (e) {
    	return { type: 'invalid' };
	}
}

export function formatDate(date: Date, config?: { dateType?: 'dateOnly' | nundefined; }): string {
	if (config?.dateType === 'dateOnly') {
		return DateTime.fromJSDate(date).toLocaleString({
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	} else {
		return DateTime.fromJSDate(date).toLocaleString({
			year: 'numeric',
			month: 'numeric',
			day: 'numeric',
			hour: 'numeric',
			minute: 'numeric'
		});	
	}
}

function getParseDateStringResultFromValidDateTime(dateTime: DateTime): ValidParsedDateResult {
	return {
		type: 'valid',
		json: dateTime.toUTC().toJSON(),
		display: dateTime.toLocaleString({ 
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric',
			timeZoneName: 'short'
		})
	}
}

interface ValidParsedDateResult {
	type: 'valid';
	json: string;
	display: string;
}

interface InvalidParsedDateResult {
    type: 'invalid';
}

interface EmptyParsedDateResult {
    type: 'none';
}

type ParsedDateResult = ValidParsedDateResult | InvalidParsedDateResult | EmptyParsedDateResult;

